import {
  FRONTEND_USER_ACTION_NAMES,
  FRONTEND_USER_ACTION_SURFACES,
  FrontendUserActionNameSchema,
  FrontendUserActionSurfaceSchema,
  type FrontendUserActionName,
  type FrontendUserActionSurface,
} from "@repo/domain-events";
import {
  getUserActionCacheOwnership,
  type UserActionCacheOwnership,
} from "~/lib/user-action-cache-ownership";

export const USER_ACTION_NAMES = FRONTEND_USER_ACTION_NAMES;
export const USER_ACTION_SURFACES = FRONTEND_USER_ACTION_SURFACES;

export type UserActionName = FrontendUserActionName;
export type UserActionSurface = FrontendUserActionSurface;

export interface UserActionContext {
  readonly action: UserActionName;
  readonly surface: UserActionSurface;
}

export interface UserActionMutationMeta extends Record<string, unknown> {
  readonly cacheOwnership: UserActionCacheOwnership;
  readonly userAction: UserActionContext;
}

type AdditionalUserActionMutationMeta = Readonly<Record<string, unknown>> & {
  readonly cacheOwnership?: never;
};

export function createUserActionMutationMeta(
  action: UserActionName,
  surface: UserActionSurface,
  additionalMeta: AdditionalUserActionMutationMeta = {},
): UserActionMutationMeta {
  return {
    ...additionalMeta,
    cacheOwnership: getUserActionCacheOwnership(action, surface),
    userAction: { action, surface },
  };
}

export function readUserActionMutationMeta(
  meta: Readonly<Record<string, unknown>> | undefined,
): UserActionContext | null {
  const value = meta?.userAction;
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const action = FrontendUserActionNameSchema.safeParse(
    Reflect.get(value, "action"),
  );
  const surface = FrontendUserActionSurfaceSchema.safeParse(
    Reflect.get(value, "surface"),
  );
  if (!action.success || !surface.success) {
    return null;
  }

  return {
    action: action.data,
    surface: surface.data,
  };
}
